/**
 * Data-collection manifest.
 *
 * The machine-readable answer to §36 ("what is monitored / what is stored / what is not
 * stored"). Deliberately a data structure rather than prose in a document, for two reasons:
 *
 * 1. The employee notice, the consent record and the admin policy screen all render from the
 *    *same* source, so the notice cannot drift from what the code actually does.
 * 2. `undocumentedFamilies` fails the agent at startup if the configured signal set contains
 *    a family with no published description. A signal we cannot explain to the person it is
 *    about does not get collected.
 */

import { KNOWN_SIGNAL_FAMILIES } from '../config.js';

export interface SignalDescription {
  readonly family: string;
  readonly title: string;
  readonly whatIsMeasured: string;
  readonly why: string;
  readonly whatIsNotCollected: string;
  readonly leavesDevice: boolean;
  readonly phase: number;
  /** True when the family is a judgement derived from other signals, not a measurement. */
  readonly inferred: boolean;
}

const DESCRIPTIONS: readonly SignalDescription[] = [
  {
    family: 'face_detected',
    title: 'Presence at the workstation',
    whatIsMeasured:
      'Whether a face is visible to the camera, and how many faces are in frame. A ' +
      'true/false value plus a count.',
    why:
      'Distinguishes "away from the workstation" from "at the workstation", so time away is ' +
      'not misread as inattention.',
    whatIsNotCollected:
      'No image, no video, no face template, no identity. The system cannot recognise who ' +
      'you are from the camera and performs no face matching.',
    leavesDevice: true,
    phase: 1,
    inferred: false,
  },
  {
    family: 'head_pose',
    title: 'Approximate head orientation',
    whatIsMeasured:
      'Three angles describing which way your head is turned (yaw), tilted up or down ' +
      '(pitch), and tipped sideways (roll), measured relative to your own calibrated ' +
      'neutral position.',
    why:
      'Estimates whether you are facing your workstation. It is one input among several and ' +
      'is never treated as proof of attention on its own.',
    whatIsNotCollected:
      'No facial landmarks, images or measurements of your appearance are stored or ' +
      'transmitted. Landmarks exist only in memory for the fraction of a second needed to ' +
      'compute the three angles.',
    leavesDevice: true,
    phase: 1,
    inferred: false,
  },
  {
    family: 'frame_quality',
    title: 'Camera conditions',
    whatIsMeasured: 'Image brightness, sharpness, and how large your face appears in frame.',
    why:
      'Poor lighting or a distant camera makes orientation unreliable. These metrics are what ' +
      'allow the system to report "insufficient data" instead of guessing and recording a ' +
      'low score.',
    whatIsNotCollected: 'No image content, background detail or scene description.',
    leavesDevice: true,
    phase: 1,
    inferred: false,
  },
  {
    family: 'attention_events',
    title: 'Attention-away events',
    whatIsMeasured:
      'Start time, end time, duration, direction and confidence for periods where your head ' +
      'was oriented away from the workstation for longer than the configured minimum.',
    why:
      'Summarises orientation over time in a reviewable form, instead of keeping a ' +
      'continuous per-second record.',
    whatIsNotCollected:
      'No record of what you were looking at, and no continuous per-frame log.',
    leavesDevice: true,
    phase: 1,
    inferred: true,
  },
  {
    family: 'gaze_direction',
    title: 'Approximate gaze direction (optional)',
    whatIsMeasured:
      'A coarse estimate of eye direction relative to the screen, in broad buckets such as ' +
      'screen-centre, screen-left or off-screen.',
    why:
      'Separates "head turned" from "eyes elsewhere", so glancing at a second work monitor is ' +
      'not counted the same as looking away.',
    whatIsNotCollected:
      'No eye images, no iris data, no per-pixel gaze point, no reading of screen content.',
    leavesDevice: true,
    phase: 2,
    inferred: false,
  },
  {
    family: 'keyboard_activity',
    title: 'Keyboard activity level',
    whatIsMeasured:
      'Whether keyboard input occurred, and an approximate events-per-minute rate.',
    why: 'Indicates active interaction with the computer.',
    whatIsNotCollected:
      'No keystrokes, no typed text, no passwords, no shortcuts. The system is not a ' +
      'keylogger: it counts events without seeing which keys they were.',
    leavesDevice: true,
    phase: 2,
    inferred: false,
  },
  {
    family: 'mouse_activity',
    title: 'Mouse activity level',
    whatIsMeasured: 'Whether mouse movement or clicks occurred, and an approximate rate.',
    why: 'Indicates active interaction with the computer.',
    whatIsNotCollected: 'No cursor coordinates, no click targets, no screen content.',
    leavesDevice: true,
    phase: 2,
    inferred: false,
  },
  {
    family: 'idle_duration',
    title: 'Time since last input',
    whatIsMeasured: 'Seconds since the most recent keyboard or mouse event.',
    why:
      'Identifies periods when the computer is idle. Idle is reported as its own state, not ' +
      'as distraction — reading, meetings and paper work are all legitimately idle.',
    whatIsNotCollected: 'Nothing about what happened during the idle period.',
    leavesDevice: true,
    phase: 2,
    inferred: false,
  },
  {
    family: 'application_category',
    title: 'Application category of the active window',
    whatIsMeasured:
      'The category of the foreground application, from a list your organisation configures ' +
      '— for example Development, Communication or Documentation.',
    why: 'Provides context for whether interaction relates to assigned work.',
    whatIsNotCollected:
      'No window titles, no document names, no URLs, no message content, no screenshots.',
    leavesDevice: true,
    phase: 2,
    inferred: false,
  },
  {
    family: 'meeting_state',
    title: 'Meeting in progress',
    whatIsMeasured: 'Whether an approved meeting application is active, as a true/false value.',
    why:
      'During meetings the ordinary attention-away rules are relaxed, because looking away ' +
      'from the screen while talking or listening is normal.',
    whatIsNotCollected:
      'No participants, no audio, no video, no meeting titles, no transcripts.',
    leavesDevice: true,
    phase: 2,
    inferred: false,
  },
];

export const SIGNAL_MANIFEST: ReadonlyMap<string, SignalDescription> = new Map(
  DESCRIPTIONS.map((description) => [description.family, description]),
);

/**
 * Categories the system does not collect, by design.
 *
 * Asserted by `test/privacy.test.ts`, which cross-checks this list against the collectable
 * signal vocabulary so that adding a field able to carry one of these breaks the build.
 */
export const NEVER_COLLECTED: readonly string[] = [
  'Camera images or video (frames are processed in memory and discarded; storage is disabled ' +
    'by default and requires a separate, audited authorisation)',
  'Face recognition, face templates or biometric identifiers',
  'Keystroke content, typed text, passwords or clipboard contents',
  'Screenshots, screen contents, window titles, document names or browsing URLs',
  'Audio or microphone data',
  'Emotion, mood, sentiment or facial-expression analysis',
  'Age, gender, ethnicity, appearance or any protected characteristic',
  'Health status, disability, fatigue or medical inference',
  'Personality, aptitude or trait assessment',
  'Location beyond the enrolled device itself',
];

/** Descriptions for the configured families, in manifest order. */
export function manifestFor(families: Iterable<string>): SignalDescription[] {
  const wanted = new Set(families);
  return DESCRIPTIONS.filter((description) => wanted.has(description.family));
}

/** Configured families that have no published description. */
export function undocumentedFamilies(families: Iterable<string>): string[] {
  return [...families].filter((family) => !SIGNAL_MANIFEST.has(family)).sort();
}

/** Known families missing from the manifest — a build-time completeness check. */
export function undocumentedKnownFamilies(): string[] {
  return [...KNOWN_SIGNAL_FAMILIES].filter((family) => !SIGNAL_MANIFEST.has(family)).sort();
}

/** Plain-text employee notice, rendered from the manifest (§36). */
export function renderNotice(input: {
  families: Iterable<string>;
  retentionDays: number;
  policyVersion: string;
}): string {
  const lines: string[] = [
    'WORK-FOCUS MONITORING — WHAT THIS SOFTWARE DOES',
    `Monitoring policy version: ${input.policyVersion}`,
    '',
    'This software estimates whether you appear to be working at your workstation.',
    'It cannot read your thoughts, emotions, mood or intent, and it does not try to.',
    'It records a small number of measurable signals, listed below, and combines them',
    'into an estimated focus score that always carries a confidence value and an',
    'explanation of the signals behind it.',
    '',
    'WHAT IS COLLECTED',
  ];

  for (const description of manifestFor(input.families)) {
    lines.push(
      `  * ${description.title}${description.inferred ? '  [inferred, not measured]' : ''}`,
      `      Measured: ${description.whatIsMeasured}`,
      `      Purpose:  ${description.why}`,
      `      Not collected: ${description.whatIsNotCollected}`,
    );
  }

  lines.push('', 'WHAT IS NEVER COLLECTED');
  for (const item of NEVER_COLLECTED) lines.push(`  * ${item}`);

  lines.push(
    '',
    'HOW IT IS HANDLED',
    '  * Camera frames are analysed on this device and are not uploaded.',
    '  * Only the derived signals above leave the device, over an encrypted channel.',
    `  * Derived data is retained for ${input.retentionDays} days, then deleted.`,
    '  * Access is role-based, and administrative access is written to an audit log.',
    '  * Scores are advisory. They are not used to make automatic decisions about you,',
    '    and any consequential review requires a named human reviewer.',
    '  * When conditions are poor or signals disagree, the system records',
    "    'unknown' or 'insufficient data' rather than assuming inattention.",
    '',
    'YOUR VISIBILITY',
    '  * An indicator is shown on this device whenever monitoring is active.',
    '  * You can view your own metrics, and the signals collected about you.',
    '',
    "Refer to your organisation's monitoring and privacy policies for the legal basis,",
    'your rights, and the contact point for questions or disputes.',
  );

  return lines.join('\n');
}

/**
 * Short, stable digest of a rendered notice.
 *
 * Stored on the consent record so it is provable *which wording* an employee saw. If the
 * notice text changes materially, the digest changes and the acknowledgement can be shown to
 * predate the current wording.
 */
export function noticeDigest(notice: string): string {
  let hash = 0x811c9dc5;
  for (let index = 0; index < notice.length; index += 1) {
    hash ^= notice.charCodeAt(index);
    hash = Math.imul(hash, 0x01000193) >>> 0;
  }
  return hash.toString(16).padStart(8, '0');
}
